import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  transportMeansUnitFromRow,
  unitFromDropdownOrCache,
} from "../../../../../CustomHeadlessTable/lcaDropdownUnit";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  getAllFlow43_2LCA,
  addFlow43_2LCA,
  updateFlow43_2LCA,
  deleteFlow43_2LCA,
} from "../../../../../../apis/Product/TransportationEoLFlow43Api";
import {
  getProductFlow43_1MeansOfTransportationDropdown,
  getProductFlow43_1PackagingPerFUDropdown,
  getProductSurplusTransportCountryDropdown,
} from "../../../../../../apis/Dropdown";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { TableCompareTrigger } from "../../../components/lcaComparison";

// Impact categories configuration (16)
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  { name: "Eutrophication, terrestrial", unit: "mol N-eq", key: "eutrophication_terrestrial" },
  { name: "Eutrophication, freshwater", unit: "kg P-eq", key: "eutrophication_freshwater" },
  { name: "Eutrophication, marine", unit: "kg N-eq", key: "eutrophication_marine" },
  { name: "Human toxicity, cancer effects", unit: "CTUh", key: "carcinogenics" },
  { name: "Human toxicity, non-cancer effects", unit: "CTUh", key: "non_carcinogenics" },
  { name: "Particulate matter", unit: "disease incidence", key: "respiratory_effects" },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  { name: "Resource use, energy carriers", unit: "MJ", key: "fossil_fuel_depletion" },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  { name: "Resource use, mineral and metals", unit: "kg Sb-eq", key: "resource_use_minerals_metals" },
  { name: "Ionising radiation, human health", unit: "kBq U235-eq", key: "ionising_radiation" },
];

const Flow43_2Table = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] = useState(0);
  const [transportMeansDropdown, setTransportMeansDropdown] = useState([]);
  const [countryDropdown, setCountryDropdown] = useState([]);
  const [packagingDropdown, setPackagingDropdown] = useState([]);
  const [unitTransportMeans, setUnitTransportMeans] = useState({});
  const [unitPackaging, setUnitPackaging] = useState({});

  // Fetch dropdowns
  useEffect(() => {
    getProductFlow43_1MeansOfTransportationDropdown({
      setDropdown: setTransportMeansDropdown,
    });
    getProductSurplusTransportCountryDropdown({
      setDropdown: setCountryDropdown,
    });
    getProductFlow43_1PackagingPerFUDropdown({
      setDropdown: setPackagingDropdown,
    });
  }, []);

  // Convert country codes to names when dropdowns are loaded
  useEffect(() => {
    if (data.length > 0 && countryDropdown.length > 0) {
      setData((prevData) => {
        let hasChanges = false;
        const convertedData = prevData.map((item) => {
          const updates = {};
          
          // Convert departure_country code to name
          if (item.departure_country && typeof item.departure_country === "string") {
            if (item.departure_country.length <= 3) {
              const foundCountry = countryDropdown.find(
                (option) => option.code === item.departure_country
              );
              if (foundCountry && item.departure_country !== foundCountry.name) {
                hasChanges = true;
                updates.departure_country = foundCountry.name;
              }
            }
          }
          
          // Convert destination_country code to name
          if (item.destination_country && typeof item.destination_country === "string") {
            if (item.destination_country.length <= 3) {
              const foundCountry = countryDropdown.find(
                (option) => option.code === item.destination_country
              );
              if (foundCountry && item.destination_country !== foundCountry.name) {
                hasChanges = true;
                updates.destination_country = foundCountry.name;
              }
            }
          }
          
          return hasChanges ? { ...item, ...updates } : item;
        });
        return hasChanges ? convertedData : prevData;
      });
    }
  }, [data, countryDropdown]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  // Update units when data or dropdown changes
  useEffect(() => {
    if (data.length > 0 && transportMeansDropdown.length > 0) {
      const newUnitTransportMeans = {};
      data.forEach((row) => {
        const transportMeansCode =
          typeof row.means_of_transportation === "string"
            ? row.means_of_transportation
            : row.means_of_transportation?.value || row.means_of_transportation?.code;
        if (transportMeansCode) {
          const selectedMeans = transportMeansDropdown.find(
            (option) =>
              !option.isCategory &&
              (option.code === transportMeansCode || option.value === transportMeansCode)
          );
          if (selectedMeans) {
            newUnitTransportMeans[row.id] = selectedMeans.unit || "xx";
          }
        }
      });
      setUnitTransportMeans(newUnitTransportMeans);
    }
  }, [data, transportMeansDropdown]);

  // Update packaging units
  useEffect(() => {
    if (data.length > 0 && packagingDropdown.length > 0) {
      const newUnitPackaging = {};
      data.forEach((row) => {
        const packagingCode =
          typeof row.packaging_per_fu_included === "string"
            ? row.packaging_per_fu_included
            : row.packaging_per_fu_included?.value || row.packaging_per_fu_included?.code;
        if (packagingCode) {
          const selectedPackaging = packagingDropdown.find(
            (option) =>
              !option.isCategory &&
              (option.code === packagingCode || option.value === packagingCode)
          );
          if (selectedPackaging) {
            newUnitPackaging[row.id] = selectedPackaging.unit || "xx";
          }
        }
      });
      setUnitPackaging(newUnitPackaging);
    }
  }, [data, packagingDropdown]);

  const dynamicPlaceholders = useMemo(
    () => ({
      means_of_transportation: {
        targetField: "transport_distance",
        getPlaceholder: (selectedValue, dropdownData, _rowData, rowId = "new") => {
          const selected = dropdownData?.find(
            (opt) => opt?.value === selectedValue || opt?.code === selectedValue
          );
          if (selected) {
            setUnitTransportMeans((prev) => ({
              ...prev,
              [rowId]: selected.unit || "xx",
            }));
          }
        },
        dropdownDataKey: "means_of_transportation",
      },
      packaging_per_fu_included: {
        targetField: "quantity_per_fu_used_for_transport",
        getPlaceholder: (selectedValue, dropdownData, _rowData, rowId = "new") => {
          const selected = dropdownData?.find(
            (opt) => opt?.value === selectedValue || opt?.code === selectedValue
          );
          if (selected) {
            setUnitPackaging((prev) => ({
              ...prev,
              [rowId]: selected.unit || "xx",
            }));
          }
        },
        dropdownDataKey: "packaging_per_fu_included",
      },
    }),
    []
  );

  const fetchData = useCallback(() => {
    if (productId) {
      getAllFlow43_2LCA({
        productId,
        setObjects: setData,
      });
    }
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(
    (editedItem) => {
      const transportMeansCode =
        typeof editedItem.means_of_transportation === "string"
          ? editedItem.means_of_transportation
          : editedItem.means_of_transportation?.value || editedItem.means_of_transportation?.code || null;
      
      // Convert country names back to codes for backend
      let departureCountryCode = editedItem.departure_country;
      if (departureCountryCode) {
        if (typeof departureCountryCode === "object") {
          departureCountryCode = departureCountryCode.code || departureCountryCode.value || departureCountryCode.name || null;
        } else if (typeof departureCountryCode === "string" && departureCountryCode.length > 3) {
          const foundCountry = countryDropdown.find(
            (option) => option.name === departureCountryCode || option.value === departureCountryCode
          );
          if (foundCountry) {
            departureCountryCode = foundCountry.code;
          }
        }
      }
      
      let destinationCountryCode = editedItem.destination_country;
      if (destinationCountryCode) {
        if (typeof destinationCountryCode === "object") {
          destinationCountryCode = destinationCountryCode.code || destinationCountryCode.value || destinationCountryCode.name || null;
        } else if (typeof destinationCountryCode === "string" && destinationCountryCode.length > 3) {
          const foundCountry = countryDropdown.find(
            (option) => option.name === destinationCountryCode || option.value === destinationCountryCode
          );
          if (foundCountry) {
            destinationCountryCode = foundCountry.code;
          }
        }
      }

      const packagingCode =
        typeof editedItem.packaging_per_fu_included === "string"
          ? editedItem.packaging_per_fu_included
          : editedItem.packaging_per_fu_included?.value || editedItem.packaging_per_fu_included?.code || null;
      
      updateFlow43_2LCA({
        productId,
        rowId: editedItem.id,
        means_of_transportation: transportMeansCode,
        city_of_departure: editedItem.city_of_departure || null,
        departure_country: departureCountryCode || null,
        city_of_destination: editedItem.city_of_destination || null,
        destination_country: destinationCountryCode || null,
        transport_distance: editedItem.transport_distance || 0,
        how_much_fu_transported: editedItem.how_much_fu_transported || 0,
        packaging_per_fu_included: packagingCode,
        quantity_per_fu_used_for_transport: editedItem.quantity_per_fu_used_for_transport || 0,
        manual_emission_factor: editedItem.manual_emission_factor || null,
        objects: data,
        setObjects: setData,
        use_swal: true,
        callback: () => {
          window.dispatchEvent(new Event('refresh-transportation-eol-43-totals'));
        },
      });
    },
    [productId, data, countryDropdown]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const transportMeansCode =
        typeof newItem.means_of_transportation === "string"
          ? newItem.means_of_transportation
          : newItem.means_of_transportation?.value || newItem.means_of_transportation?.code || null;
      
      // Convert country names back to codes for backend
      let departureCountryCode = newItem.departure_country;
      if (departureCountryCode) {
        if (typeof departureCountryCode === "object") {
          departureCountryCode = departureCountryCode.code || departureCountryCode.value || departureCountryCode.name || null;
        } else if (typeof departureCountryCode === "string" && departureCountryCode.length > 3) {
          const foundCountry = countryDropdown.find(
            (option) => option.name === departureCountryCode || option.value === departureCountryCode
          );
          if (foundCountry) {
            departureCountryCode = foundCountry.code;
          }
        }
      }
      
      let destinationCountryCode = newItem.destination_country;
      if (destinationCountryCode) {
        if (typeof destinationCountryCode === "object") {
          destinationCountryCode = destinationCountryCode.code || destinationCountryCode.value || destinationCountryCode.name || null;
        } else if (typeof destinationCountryCode === "string" && destinationCountryCode.length > 3) {
          const foundCountry = countryDropdown.find(
            (option) => option.name === destinationCountryCode || option.value === destinationCountryCode
          );
          if (foundCountry) {
            destinationCountryCode = foundCountry.code;
          }
        }
      }

      const packagingCode =
        typeof newItem.packaging_per_fu_included === "string"
          ? newItem.packaging_per_fu_included
          : newItem.packaging_per_fu_included?.value || newItem.packaging_per_fu_included?.code || null;
      
      addFlow43_2LCA({
        productId,
        means_of_transportation: transportMeansCode,
        city_of_departure: newItem.city_of_departure || null,
        departure_country: departureCountryCode || null,
        city_of_destination: newItem.city_of_destination || null,
        destination_country: destinationCountryCode || null,
        transport_distance: newItem.transport_distance || 0,
        how_much_fu_transported: newItem.how_much_fu_transported || 0,
        packaging_per_fu_included: packagingCode,
        quantity_per_fu_used_for_transport: newItem.quantity_per_fu_used_for_transport || 0,
        manual_emission_factor: newItem.manual_emission_factor || null,
        callback: () => {
          fetchData();
          window.dispatchEvent(new Event('refresh-transportation-eol-43-totals'));
        },
        use_swal: true,
      });
    },
    [productId, fetchData, countryDropdown]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteFlow43_2LCA({
        productId,
        rowId: itemToDelete.id,
        callback: () => {
          fetchData();
          window.dispatchEvent(new Event('refresh-transportation-eol-43-totals'));
        },
      });
    },
    [productId, fetchData]
  );

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const impactCategoryAccessor = currentImpactCategory.key;

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "means_of_transportation",
        accessor: "means_of_transportation",
        header: `${t("MEANS_OF_TRANSPORTATION") || "Means of transportation"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_TRANSPORT_MEANS") || "Select transport means...",
        unit: (rowId, rowData) =>
          transportMeansUnitFromRow(
            rowData,
            transportMeansDropdown,
            unitTransportMeans,
            rowId,
            "means_of_transportation"
          ),
      },
      {
        id: "city_of_departure",
        accessor: "city_of_departure",
        header: t("CITY_OF_DEPARTURE") || "City of departure",
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
        optional: true,
      },
      {
        id: "departure_country",
        accessor: "departure_country",
        header: `${t("CHOOSE_COUNTRY_DEPARTURE") || "Choose country of departure"} *`,
        type: "dropdown",
        placeholder: t("SELECT_COUNTRY") || "Select country...",
        optional: true,
      },
      {
        id: "city_of_destination",
        accessor: "city_of_destination",
        header: t("CITY_OF_DESTINATION") || "City of destination",
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
        optional: true,
      },
      {
        id: "destination_country",
        accessor: "destination_country",
        header: `${t("CHOOSE_COUNTRY_ARRIVAL") || "Choose country of arrival"} *`,
        type: "dropdown",
        placeholder: t("SELECT_COUNTRY") || "Select country...",
        optional: true,
      },
      {
        id: "transport_distance",
        accessor: "transport_distance",
        header: `${t("TRANSPORT_DISTANCE") || "Transport distance"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: () => "km",
      },
      {
        id: "how_much_fu_transported",
        accessor: "how_much_fu_transported",
        header: `${t("HOW_MUCH_FU_TRANSPORTED") || "How much of the FU is transported?"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: () => "kg",
      },
      {
        id: "packaging_per_fu_included",
        accessor: "packaging_per_fu_included",
        header:
          t("PACKAGING_PER_FU_INCLUDED_43_2") ||
          "Packaging per FU included in the transport from the demolition site to the facility where the product is sorted, cleaned, or pre-treated for recycling",
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_PACKAGING") || "Select packaging...",
        optional: true,
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "packaging_per_fu_included",
            packagingDropdown,
            unitPackaging,
            rowId
          ),
      },
      {
        id: "quantity_per_fu_used_for_transport",
        accessor: "quantity_per_fu_used_for_transport",
        header: `${t("QUANTITY_PER_FU_USED_FOR_TRANSPORT") || "Quantity per FU used for transport"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "packaging_per_fu_included",
            packagingDropdown,
            unitPackaging,
            rowId,
            "kg"
          ),
      },
      {
        id: "manual_emission_factor",
        accessor: "manual_emission_factor",
        header: `${t("MANUAL_EMISSION_FACTOR_43") || "Manual: xx/tkm (e.g. for specific means of transportation)"}`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        optional: true,
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton size="small" onClick={handlePreviousImpactCategory} style={{ padding: "4px" }}>
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton size="small" onClick={handleNextImpactCategory} style={{ padding: "4px" }}>
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
            <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true,
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      t,
      unitTransportMeans,
      unitPackaging,
      transportMeansDropdown,
      packagingDropdown,
      data,
      currentImpactCategory,
      currentImpactCategoryIndex,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
    ]
  );

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: { label: `Total ${currentImpactCategory.name}` },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      means_of_transportation: transportMeansDropdown || [],
      departure_country: countryDropdown || [],
      destination_country: countryDropdown || [],
      packaging_per_fu_included: packagingDropdown || [],
    }),
    [transportMeansDropdown, countryDropdown, packagingDropdown]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={useMemo(
        () => ({ [impactCategoryAccessor]: CustomInputComponent }),
        [impactCategoryAccessor]
      )}
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default Flow43_2Table;

